import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { SharepointDataUpdatedAction } from 'constants/BackgroundScript';
import { Maybe } from 'types/UtilityTypes';

import type { SharepointAgileRow } from '../types/Sharepoint';
import {
  normalizeSharepointAgileRows,
  pullSharepointEwaAgileRowsFromServiceWorker,
} from '../utils/getSharepointEwaAgileRows';
import { useSharepointLoadBuildingProcessing } from './SharepointLoadBuildingProcessingProvider';

export type SharepointEwaAgileRowsState = {
  agileRows: Maybe<SharepointAgileRow[]>;
};

const defaultEwaState: SharepointEwaAgileRowsState = { agileRows: null };

export const SharepointEwaAgileRowsContext =
  createContext<SharepointEwaAgileRowsState>(defaultEwaState);

export function useSharepointEwaAgileRowsState(): SharepointEwaAgileRowsState {
  return useContext(SharepointEwaAgileRowsContext);
}

type SharepointEwaAgileRowsProviderProps = {
  enabled: boolean;
  children: ReactNode;
};

function broadcastTargetsHostTab(
  broadcastTabId: number | undefined,
  hostTabId: number | undefined
): boolean {
  if (broadcastTabId == null) {
    return false;
  }
  return hostTabId == null || broadcastTabId === hostTabId;
}

/**
 * McLeod / Syfan Agile: exposes the service worker's last EWA prefetch parse as `agileRows`.
 * Row data only updates when Excel prefetch runs again (typically after the user refreshes
 * or scrolls the workbook tab). Does not rescrape Excel or sync on worksheet tab changes.
 * Turvo uses {@link SharepointWorkbookGraphProvider} for row bodies.
 */
export function SharepointEwaAgileRowsProvider({
  enabled,
  children,
}: SharepointEwaAgileRowsProviderProps) {
  const [agileRows, setAgileRows] = useState<Maybe<SharepointAgileRow[]>>(null);
  const hostTabIdRef = useRef<number | undefined>(undefined);
  const { setAgileLoading } = useSharepointLoadBuildingProcessing();

  const syncFromServiceWorker = useCallback(async () => {
    setAgileLoading(true);
    try {
      const { hostTabId, rows } =
        await pullSharepointEwaAgileRowsFromServiceWorker();
      if (hostTabId != null) {
        hostTabIdRef.current = hostTabId;
      }
      setAgileRows(rows);
    } finally {
      setAgileLoading(false);
    }
  }, [setAgileLoading]);

  useEffect(() => {
    if (!enabled) {
      setAgileRows(null);
      hostTabIdRef.current = undefined;
      return;
    }

    void syncFromServiceWorker();
  }, [enabled, syncFromServiceWorker]);

  useEffect(() => {
    if (
      !enabled ||
      typeof chrome === 'undefined' ||
      !chrome.runtime?.onMessage?.addListener
    ) {
      return;
    }

    const onRuntimeMessage = (msg: {
      action?: string;
      tabId?: number;
      data?: unknown;
    }) => {
      if (!broadcastTargetsHostTab(msg.tabId, hostTabIdRef.current)) {
        return;
      }
      if (msg.tabId != null) {
        hostTabIdRef.current = msg.tabId;
      }

      if (msg.action === SharepointDataUpdatedAction) {
        const rows = normalizeSharepointAgileRows(msg.data);
        setAgileRows(rows.length > 0 ? rows : null);
      }
    };

    chrome.runtime.onMessage.addListener(onRuntimeMessage);
    return () => chrome.runtime.onMessage.removeListener(onRuntimeMessage);
  }, [enabled]);

  const value = useMemo(() => ({ agileRows }), [agileRows]);

  return (
    <SharepointEwaAgileRowsContext.Provider value={value}>
      {children}
    </SharepointEwaAgileRowsContext.Provider>
  );
}
